import { F, STATE_STRIDE } from './constants.ts';
import type { BrushStroke } from './types.ts';

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** Apply one radial stamp while preserving `height = bedrock + soil`. */
export function applyBrushToState(
  state: Float32Array,
  width: number,
  cellSize: number,
  stroke: BrushStroke,
): boolean {
  const radius = clamp(stroke.radius, 0.002, 0.25) * width;
  // Normalised coordinates address cell centres, including the outermost cells.
  const cx = 0.5 + clamp(stroke.u, 0, 1) * (width - 1);
  const cy = 0.5 + clamp(stroke.v, 0, 1) * (width - 1);
  const radius2 = radius * radius;
  const i0 = Math.max(0, Math.floor(cx - radius));
  const i1 = Math.min(width - 1, Math.ceil(cx + radius));
  const j0 = Math.max(0, Math.floor(cy - radius));
  const j1 = Math.min(width - 1, Math.ceil(cy + radius));
  let touched = false;

  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const dx = i + 0.5 - cx;
      const dy = j + 0.5 - cy;
      const distance2 = dx * dx + dy * dy;
      if (distance2 > radius2) continue;
      const t = Math.sqrt(distance2) / radius;
      const falloff = 1 - t * t * (3 - 2 * t);
      const amount = stroke.strength * falloff;
      const b = (j * width + i) * STATE_STRIDE;
      touched = true;

      switch (stroke.mode) {
        case 'raise':
          state[b + F.height] += amount;
          state[b + F.bedrock] += amount;
          break;
        case 'lower': {
          const height = Math.max(0, state[b + F.height] - amount);
          const soil = Math.min(state[b + F.soil], height);
          state[b + F.height] = height;
          state[b + F.soil] = soil;
          state[b + F.bedrock] = height - soil;
          break;
        }
        case 'water':
          state[b + F.water] = Math.max(0, state[b + F.water] + amount);
          break;
        case 'sediment': {
          const soil = Math.max(0, state[b + F.soil] + amount);
          state[b + F.soil] = soil;
          state[b + F.height] = state[b + F.bedrock] + soil;
          break;
        }
        case 'hardness':
          state[b + F.hardness] = clamp(state[b + F.hardness] + amount * 0.1, 0.25, 3);
          break;
        case 'smooth': {
          const im = i > 0 ? i - 1 : i;
          const ip = i + 1 < width ? i + 1 : i;
          const jm = j > 0 ? j - 1 : j;
          const jp = j + 1 < width ? j + 1 : j;
          const mean =
            (state[(j * width + im) * STATE_STRIDE + F.height] +
              state[(j * width + ip) * STATE_STRIDE + F.height] +
              state[(jm * width + i) * STATE_STRIDE + F.height] +
              state[(jp * width + i) * STATE_STRIDE + F.height]) *
            0.25;
          const oldHeight = state[b + F.height];
          const height = oldHeight + (mean - oldHeight) * clamp(amount / Math.max(1, cellSize), 0, 1);
          const oldSoil = state[b + F.soil];
          const soil = height >= oldHeight ? oldSoil + (height - oldHeight) : Math.min(oldSoil, height);
          state[b + F.height] = height;
          state[b + F.soil] = soil;
          state[b + F.bedrock] = height - soil;
          break;
        }
      }
    }
  }
  return touched;
}
